import { z } from 'zod';
import { JobTypeSchema } from './enums.schema';
import { JobSchema } from './job.schema';

/**
 * Job API Request/Response Schemas
 * Defines validation schemas for job-related API endpoints
 */

/**
 * Provider parameters that multiply how many outputs one request returns.
 *
 * The platform bills per variant and issues one provider request per billed
 * variant, so any of these keys arriving in user settings would hand back more
 * output than was paid for. They are never taken from the client: the billed
 * count is the only thing that decides how much a provider is asked for.
 *
 * Names are matched case- and underscore-insensitively, so `numImages` and
 * `num_images` are the same key.
 */
export const OUTPUT_COUNT_SETTING_KEYS = [
  'n',
  'samples',
  'num_images',
  'num_outputs',
  'num_samples',
  'batch_size',
  'max_images',
  'image_count',
  // Names the audited fal contracts actually use for the same thing:
  // number_of_images (omni-zero), num_results (bria/product-shot, which returns
  // num_results x 10 images), num_images_per_size (smart-resize).
  'number_of_images',
  'num_results',
  'num_images_per_size',
  // qwen-image-layered and its lora variant return one image per layer — priced
  // per image, default 4, up to 10, so one paid variant came back as ten images.
  'num_layers',
] as const;

/**
 * Names deliberately left out, from the same census of the 1,474 catalogued endpoints:
 *
 * - `num_segments` (longcat single-avatar) sets the *length* of one video, not how many
 *   videos come back, and it is what prices a `video segments` model. Stripping it would
 *   both change the output and undo the pricing (see pricing-units.ts).
 * - `num_frames`, `num_inference_steps`, `num_layers`' neighbours `num_speakers`,
 *   `face_count`, `max_num_objects` describe the content of a single output.
 * - `agentic_samples_per_iteration` (nvidia/cosmos-3-super) generates candidates and returns
 *   only the winner, so it multiplies provider *spend* without multiplying delivered output.
 *   It is a pricing gap, not an over-delivery one, and pinning it here would silently cap a
 *   quality control the model exposes.
 */

/** Upper bound on variants a single job may bill and produce. */
export const MAX_VARIANT_COUNT = 10;

const OUTPUT_COUNT_LOOKUP = new Set<string>(
  OUTPUT_COUNT_SETTING_KEYS.map((key) => key.replace(/_/g, '')),
);

/** True when a settings key controls how many outputs a provider returns. */
export function isOutputCountSettingKey(key: string): boolean {
  return OUTPUT_COUNT_LOOKUP.has(key.toLowerCase().replace(/[_-]/g, ''));
}

/**
 * Remove output-count keys from a settings record, leaving every other
 * setting untouched. Returns the names that were removed so callers can log
 * or report them.
 */
export function stripOutputCountSettings(
  settings: Record<string, unknown> | undefined,
): { settings: Record<string, unknown> | undefined; removed: string[] } {
  if (!settings) return { settings, removed: [] };

  const removed: string[] = [];
  const kept: Record<string, unknown> = {};
  for (const [key, value] of Object.entries(settings)) {
    if (isOutputCountSettingKey(key)) {
      removed.push(key);
      continue;
    }
    kept[key] = value;
  }

  return removed.length > 0 ? { settings: kept, removed } : { settings, removed };
}

// Job creation request
export const CreateJobRequestSchema = z.object({
  type: JobTypeSchema,
  canvasId: z.string(),
  nodeId: z.string(),
  /**
   * Canvas family and tool identity. Optional so hosted jobs that are not a
   * canvas node still parse. When present, the job service refuses non-operations
   * before they are charged or enqueued.
   */
  nodeType: z.string().max(64).optional(),
  purpose: z.string().max(64).optional(),
  subPurpose: z.string().max(64).optional(),
  sourceFamily: z.string().max(64).optional(),
  input: z
    .object({
      prompt: z.string(),
      modelId: z.string(),
      provider: z.string().optional(),
      settings: z.record(z.string(), z.unknown()).optional(),
      variantCount: z.number().int().min(1).max(MAX_VARIANT_COUNT).default(1),
    })
    // variantCount is what the caller is billed for, so output-count keys are
    // dropped here instead of merged. They are normalised rather than refused:
    // several live models (seedream's max_images, fashn tryon's num_samples)
    // still render these as node settings, and a 400 would make those models
    // unusable while the provider request is already pinned to the billed count.
    .transform((input) => {
      const { settings } = stripOutputCountSettings(input.settings);
      return settings === input.settings ? input : { ...input, settings };
    }),
});

// Job creation response
export const CreateJobResponseSchema = z.object({
  jobId: z.string(),
  status: z.literal('PENDING'),
  creditsCharged: z.number(),
  createdAt: z.string().datetime(),
  lowBalance: z.boolean().optional(),
  warning: z.enum(['LOW_BALANCE', 'ZERO_BALANCE']).optional(),
  remainingBalance: z.string().optional(),
});

// Canvas jobs query params
export const CanvasJobsQuerySchema = z.object({
  status: z.string().optional(), // comma-separated statuses
  limit: z.number().int().min(1).max(100).default(50),
});

// Canvas jobs response
export const CanvasJobsResponseSchema = z.object({
  jobs: z.array(JobSchema),
  total: z.number(),
});

// Cancel response. A cancel that loses the race to a worker's FAILED still succeeds and is
// refunded, but the row reads FAILED — reporting CANCELED there would be a lie.
export const CancelJobResponseSchema = z.object({
  jobId: z.string(),
  status: z.enum(['CANCELED', 'FAILED']),
  creditsRefunded: z.number(),
});

// Retry response
export const RetryJobResponseSchema = z.object({
  newJobId: z.string(),
  originalJobId: z.string(),
  status: z.literal('PENDING'),
  creditsCharged: z.number(),
});

// Export TypeScript types
export type CreateJobRequest = z.infer<typeof CreateJobRequestSchema>;
export type CreateJobResponse = z.infer<typeof CreateJobResponseSchema>;
export type CanvasJobsQuery = z.infer<typeof CanvasJobsQuerySchema>;
export type CanvasJobsResponse = z.infer<typeof CanvasJobsResponseSchema>;
export type CancelJobResponse = z.infer<typeof CancelJobResponseSchema>;
export type RetryJobResponse = z.infer<typeof RetryJobResponseSchema>;
